import { useEffect, useRef, useState } from "react";
import AddClubOverview from "./AddClubOverview";
import AddClubAudienceForm from "./AddClubAudienceForm";
import AddClubPricingForm from "./AddClubPricingForm";
import AddClubTermsMediaForm from "./AddClubTermsMediaForm";
import { useGlobalStates } from "../../utlis/ClubsContext";

const AddClubForm = ({ formKey, onClose, onSave }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isDirty, setIsDirty] = useState(false);

  const initialFormData = {
    // Overview
    logo: null,
    clubName: "",
    clubTagline: "",
    description: "",
    emailAddress: "",
    phoneNumber: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    latitude: null,
    longitude: null,
    extraAddresses: [],
    clubImages: [],
    whyPartnerSections: {
      image: null,
      items: [
        { heading: "", description: "" },
        { heading: "", description: "" },
        { heading: "", description: "" },
      ],
    },
    advertiserBenefitsTitle: "",
    advertiserBenefitsDescription: "",

    // Audience & Performance
    totalMembers: "",
    monthlyVisits: "",
    avgHouseholdIncome: "",
    avgDailyVisit: "",
    screensAcrossClub: "",
    avgDwellTime: "",
    genderMale: "",
    genderFemale: "",
    ageUnder18: "",
    age18to24: "",
    age25to34: "",
    age35to44: "",
    age45to54: "",
    age55to64: "",
    age65Plus: "",
    dailyImpressions: "",
    monthlyImpressions: "",
    performanceAvgCPM: "",
    engagementRate: "",
    collegePercentage: "",

    // Pricing & Add-Ons
    duration15sec1Month: "",
    duration15sec6Month: "",
    duration15sec12Month: "",
    duration30sec1Month: "",
    duration30sec6Month: "",
    duration30sec12Month: "",
    dayPartStatus: false,
    daypartOpen10AM: "",
    dp10AmLimit: "",
    daypart10AMto4PM: "",
    dp10_4Limit: "",
    daypart4PMClose: "",
    dp4PmLimit: "",
    daypartWeekendsOnly: "",
    dpWeekendAmLimit: "",
    geoTargetingRadioButton: false,
    geoTargetingSetupFee: "",
    geoTargetingMonthlyFee: "",
    designServiceRadioButton: false,
    designServiceFee: "",
    clubHandleDesign: false,
    drsDesignSupport: false,

    // Terms & Media
    termsAndConditions: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [completedStep, setCompletedStep] = useState(0);
  const { setClubId } = useGlobalStates();
  const topRef = useRef(null);

useEffect(() => {
  topRef.current?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}, [activeStep]);

  useEffect(() => {
    setClubId(null);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Prevent starting with 0
    if (name !== "zipCode" && value.length === 1 && value === "0") {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setIsDirty(true);
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const steps = [
    { id: 0, label: "Overview", locked: false },
    { id: 1, label: "Audience & Performance", locked: completedStep < 1},
    { id: 2, label: "Pricing & Add-Ons", locked: completedStep < 2 },
    { id: 3, label: "Terms & Media", locked: completedStep < 3},
  ];

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    } else {
      // Save form
      onSave(formData); 
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const updateFormData = (data) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  // Clear all errors when tab/step changes
  useEffect(() => {
    setErrors({});
  }, [activeStep]);

  const resetFormData = () => {
    setFormData(structuredClone(initialFormData));
  };

  return (
    <div className="bg-lightbg min-h-screen">
      {/* Header */}

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-black tracking-[0] leading-[normal]"
        ref={topRef}>
          Add Club Details
        </h1>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 5L5 15M5 5L15 15"
              stroke="#4C4C4C"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Tabs */}
      <div className=" main-shadow rounded-[12px] overflow-hidden mb-4 mt-6 bg-white border-b border-gray-200">
        <div className="flex gap-11 px-6">
          {steps.map((step) => (
            <button
              key={step.id}
              disabled={step.locked}
              onClick={() => {
                if (!step.locked) setActiveStep(step.id);
              }}
              className={`py-4 text-base font-medium tracking-[0] leading-[normal] relative transition-colors
      ${
        step.locked
          ? "text-gray-400 cursor-not-allowed opacity-60"
          : activeStep === step.id
          ? "text-primary"
          : "text-textcolor hover:text-black"
      }
    `}
            >
              {step.label}
              {activeStep === step.id && (
                <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-primary" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <div className="pb-6">
        {activeStep === 0 && (
          <AddClubOverview
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            setErrors={setErrors}
            handleInputChange={handleInputChange}
            activeStep={activeStep}
            steps={steps}
            handleNext={handleNext}
            setCompletedStep={setCompletedStep}
            completedStep={completedStep}
            setIsDirty={setIsDirty}
            isDirty={isDirty}
          />
        )}
        {activeStep === 1 && (
          <AddClubAudienceForm
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            setErrors={setErrors}
            handleInputChange={handleInputChange}
            activeStep={activeStep}
            steps={steps}
            handleNext={handleNext}
            setCompletedStep={setCompletedStep}
            completedStep={completedStep}
            setIsDirty={setIsDirty}
            isDirty={isDirty}
          />
        )}
        {activeStep === 2 && (
          <AddClubPricingForm
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            setErrors={setErrors}
            handleInputChange={handleInputChange}
            activeStep={activeStep}
            steps={steps}
            handleNext={handleNext}
            setCompletedStep={setCompletedStep}
            completedStep={completedStep}
            setIsDirty={setIsDirty}
            isDirty={isDirty}
          />
        )}
        {activeStep === 3 && (
          <AddClubTermsMediaForm
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            setErrors={setErrors}
            handleInputChange={handleInputChange}
            activeStep={activeStep}
            steps={steps}
            handleNext={handleNext}
            setCompletedStep={setCompletedStep}
            completedStep={completedStep}
          />
        )}

        {/* Action Buttons */}
        {/* <div className="flex items-center justify-between mt-6">
          <button
            onClick={handleBack}
            disabled={activeStep === 0}
            className={`cursor-pointer px-2 h-[60px] w-[200px] border border-primary text-primary font-medium text-lg rounded-full transition-colors ${
              activeStep === 0
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-primary hover:text-white'
            }`}
          >
            Back
          </button>
          <button
            onClick={handleNext}
            className="cursor-pointer px-2 h-[60px] w-[200px] border bg-primary hover:bg-hover text-white rounded-full text-primary font-medium text-lg transition-colors"
          >
            {activeStep === steps.length - 1 ? 'Save' : 'Next'}
          </button>
        </div> */}
      </div>
    </div>
  );
};

export default AddClubForm;
